import React, { useEffect, useState } from "react";
import api from "../utils/axiosConfig";
import "../css/leaveRequestTable.css";
import localforage from "localforage";
import Dashboard from "./Dashboard";
import ReactPaginate from "react-paginate";

const API_URL = process.env.REACT_APP_API_URL;

// Multi-leg helpers: use the arrays when present, else fall back to the legacy
// single fields so older records still render.
const reviewerLabel = (t) => {
  switch (t.reviewerStatus) {
    case "approved":
      // Auto-cleared because the reporting manager raised it themselves.
      return t.isGroupRequest &&
        (t.reviewedByEmail || "").toLowerCase() === (t.requestedByEmail || "").toLowerCase()
        ? "Reviewer Approved (RM-raised)"
        : "Reviewer Approved";
    case "rejected":
      return "Reviewer Rejected";
    case "not_required":
      return "No Reviewer";
    default:
      return "Pending Reviewer";
  }
};

const tvFmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("en-GB", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
        timeZone: "Asia/Kolkata",
      })
    : "NA";

// Expand a record into one entry per leg for the sheet-style table, pairing each
// leg with its accommodation (per-leg fields first, then the old accommodations[]
// list by index, then legacy single fields).
const tvExpandLegs = (t) => {
  const stays = Array.isArray(t.accommodations) ? t.accommodations : [];
  if (Array.isArray(t.travelLegs) && t.travelLegs.length) {
    return t.travelLegs.map((l, idx) => {
      const s = stays[idx] || null;
      return {
        fromLocation: l.fromLocation || "",
        toLocation: l.toLocation || "",
        travelDate: l.travelDate || null,
        accommodationPlace: l.accommodationPlace || (s && s.location) || "",
        accommodationStartDate: l.accommodationStartDate || (s && s.startDate) || null,
        accommodationEndDate: l.accommodationEndDate || (s && s.endDate) || null,
      };
    });
  }
  const s = stays[0] || null;
  return [
    {
      fromLocation: t.fromLocation || "",
      toLocation: t.toLocation || "",
      travelDate: t.travelDate || null,
      accommodationPlace: (s && s.location) || "",
      accommodationStartDate: (s && s.startDate) || t.accommodationStartDate || null,
      accommodationEndDate: (s && s.endDate) || t.accommodationEndDate || null,
    },
  ];
};



const TravelRequestUserData = () => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;
  const [expandedRows, setExpandedRows] = useState({});

  const pageCount = Math.ceil(leaveRequests.length / itemsPerPage);
  const displayedLeaveRequests = leaveRequests.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  useEffect(() => {
    const fetchLeaveRequests = async () => {
      try {
        const token = await localforage.getItem("token");
        const response = await api.get(
          `${API_URL}/travel/travel-requests`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setLeaveRequests(response.data.leaveRequests);
      } catch (err) {
        console.error("Error fetching travel requests:", err);
        setError("Failed to fetch travel requests. Please try again later.");
      }
    };
    fetchLeaveRequests();
  }, []);

  const handlePageClick = (data) => {
    setCurrentPage(data.selected);
  };

  const handleDeleteRequest = async (id) => {
    if (
      !window.confirm(
        "Delete this travel request? This cannot be undone."
      )
    )
      return;
    try {
      const token = await localforage.getItem("token");
      await api.delete(`${API_URL}/travel/delete-mom/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeaveRequests((prev) => prev.filter((r) => r._id !== id));
    } catch (err) {
      alert(
        err.response?.data?.error ||
          "Could not delete this request. You can only delete requests you raised."
      );
    }
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const formatLeaveType = (type) =>
    type.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());

  const truncateText = (text, id) => {
    const words = text.split(" ");
    if (expandedRows[id] || words.length <= 5) {
      return (
        <>
          {text}{" "}
          {words.length > 5 && (
            <span
              className="toggle-text"
              onClick={() => toggleRowExpansion(id)}
            >
              Show Less
            </span>
          )}
        </>
      );
    }
    return (
      <>
        {words.slice(0, 5).join(" ")}...{" "}
        <span className="toggle-text" onClick={() => toggleRowExpansion(id)}>
          Show More
        </span>
      </>
    );
  };

  const capitalizeWords = (str) => {
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <>
      <Dashboard />
      <div>
        <h2>Travel Requests</h2>

        {error && <p className="error-message">{error}</p>}
        {leaveRequests.length > 0 ? (
          <>
            <div className="intervention-table">
              <table>
                <thead>
                  <tr>
                    <th>S.N</th>
                    <th>Team Member Name</th>
                    <th>Phone Number</th>
                    <th>Travel From</th>
                    <th>Travel To</th>
                    <th>Travel Date &amp; Time</th>
                    <th>Accommodation Place</th>
                    <th>Accommodation Start Date &amp; Time</th>
                    <th>Accommodation End Date &amp; Time</th>
                    <th>Request Type</th>
                    <th>Purpose Of Travel</th>
                    <th>Event Location</th>
                    <th>Event Details</th>
                    <th>Remarks</th>
                    <th>Requested By</th>
                    <th>Group</th>
                    <th>Travel Code</th>
                    <th>Reviewer Status</th>
                    <th>Travel Status</th>
                    <th>Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    let sn = 0;
                    return displayedLeaveRequests.map((travel) => {
                      const legs = tvExpandLegs(travel);
                      return legs.map((leg, li) => {
                        sn += 1;
                        return (
                          <tr key={travel._id + "-" + li}>
                            <td>{sn}</td>
                            <td>{travel.name || "—"}</td>
                            <td>{travel.employeePhoneNumber || "—"}</td>
                            <td>{leg.fromLocation || "—"}</td>
                            <td>{leg.toLocation || "—"}</td>
                            <td>{tvFmtDateTime(leg.travelDate)}</td>
                            <td>{leg.accommodationPlace || "—"}</td>
                            <td>{tvFmtDateTime(leg.accommodationStartDate)}</td>
                            <td>{tvFmtDateTime(leg.accommodationEndDate)}</td>
                            {li === 0 && (
                              <>
                                <td rowSpan={legs.length}>{formatLeaveType(travel.requestType)}</td>
                                <td rowSpan={legs.length}>{travel.purposeOfTravel}</td>
                                <td rowSpan={legs.length}>{travel.eventLocation}</td>
                                <td rowSpan={legs.length}>{truncateText(travel.eventDetails || "", travel._id)}</td>
                                <td rowSpan={legs.length}>{truncateText(travel.remarks || "", travel._id)}</td>
                                <td rowSpan={legs.length}>{travel.requestedByName || "—"}</td>
                                <td rowSpan={legs.length}>{travel.isGroupRequest ? "Group" : "Self"}</td>
                                <td rowSpan={legs.length}>{travel.travelCode}</td>
                                <td
                                  rowSpan={legs.length}
                                  className={
                                    travel.reviewerStatus === "approved"
                                      ? "status-approved"
                                      : travel.reviewerStatus === "rejected"
                                      ? "status-not-approved"
                                      : "status-pending"
                                  }
                                >
                                  {reviewerLabel(travel)}
                                </td>
                                <td
                                  rowSpan={legs.length}
                                  className={
                                    travel.requestStatus === "approved"
                                      ? "status-approved"
                                      : travel.requestStatus === "not approved"
                                      ? "status-not-approved"
                                      : "status-pending"
                                  }
                                >
                                  {capitalizeWords(travel.requestStatus)}
                                </td>
                                <td rowSpan={legs.length}>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteRequest(travel._id)}
                                    style={{
                                      background: "#b91c1c",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "6px",
                                      padding: "6px 12px",
                                      cursor: "pointer",
                                      fontWeight: 600,
                                    }}
                                    title="Delete this request"
                                  >
                                    Delete
                                  </button>
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      });
                    });
                  })()}
                </tbody>
              </table>
            </div>
            <ReactPaginate
              previousLabel={"Previous"}
              nextLabel={"Next"}
              breakLabel={"..."}
              pageCount={pageCount}
              marginPagesDisplayed={2}
              pageRangeDisplayed={3}
              onPageChange={handlePageClick}
              containerClassName={"pagination"}
              activeClassName={"active"}
              previousLinkClassName={"prev-link"}
              nextLinkClassName={"next-link"}
              disabledClassName={"disabled"}
            />
          </>
        ) : (
          <p>No travel requests found.</p>
        )}
      </div>
    </>
  );
};

export default TravelRequestUserData;
